import { Allotment } from "allotment";
import Color from "color";
import { toLonLat } from "ol/proj";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ButtonGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { RLayerVector, useOL } from "rlayers";
import Button from "../../common/components/Button";
import { DefaultButton } from "../../common/components/DefaultButton";
import { Col, Row } from "../../common/components/Grid";
import Icon from "../../common/components/Icon";
import { SettingsDisplay, useSettingsContext } from "../../common/components/SettingsPanels";
import { SortDialog } from "../../common/components/Sort";
import TextInput from "../../common/components/TextInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { ReactNodeDialog } from "../../common/utils/dialogUtlities";
import { dynamicFilter, dynamicSort } from "../../common/utils/tableUtilities";
import { canInsertLocation, canRemoveLocation, canUpdateLocation, createLocation, removeLocation, updateLocation, useLocations } from "../../reducers/locationSlice";
import { altCoord, geoSearch } from "../../utils/geoUtilities";
import { BaseMap, DefaultView } from "../GeoMap";
import LevelDropdown from "../LevelDropdown";
import SiteDropdown from "../SiteDropdown";
import TypeDropdown from "../TypeDropdown";
import { LocationMarker } from "../map/Marker";
import PropTypes from "prop-types";
import LocationDetails from "../LocationDetails";
import { deepClone, deepMerge } from "../../common/utils/objectUtilities";

const defaultSort = ['name']

const LocationButton = ({ location, selected, onClick, showSite, showLevel, showType, setNewLocation }) => {
    const { pushPanel } = useSettingsContext();
    const clientConfig = useClientConfig();
    const { t } = useTranslation();

    let nameSize = 12;
    if (showSite) {
        nameSize -= 2;
    }
    if (showLevel) {
        nameSize -= 2;
    }
    if (showType) {
        nameSize -= 2;
    }

    let textColor, backgroundColor;
    if (location.color) {
      try {
        textColor = Color(location.color).isLight() ? '#000000' : '#ffffff';
        backgroundColor = Color(location.color).hex();
      } catch {
        textColor = '#000000';
        backgroundColor = '#ffffff'
      }
    }

    return <Button key={'location_edit_btn_' + location.internalid } className="m-2" variant={selected ? "dark" : "outline-dark"} onClick={() => { pushPanel(1, setNewLocation); onClick(location); }}>
        <Row>
            <Col xs={1} className="text-left">{location.hidden && <span className="mx-2"><Icon variant="solid" kind="eye-slash"></Icon></span>}<Icon literal={location.icon} style={{padding:5, backgroundColor, color: textColor}}></Icon></Col>
            <Col>
                <Row className="text-left">
                    <Col style={{ whiteSpace: 'normal'}} md={nameSize}>{location.name + (clientConfig.enableGrid && location.gridx ? ` [${location.gridx ?? '?'},${location.gridy ?? t('unknown')}]` : '')}</Col>
                    {showSite && <Col style={{ whiteSpace: 'normal' }} md={2}>{location.site}</Col>}
                    {showLevel && <Col style={{ whiteSpace: 'normal' }} md={2}>{location.level}</Col>}
                    {showType && <Col style={{ whiteSpace: 'normal' }} md={2}>{location.type}</Col>}
                </Row>
            </Col>
        </Row>
    </Button>
}

const NewLocationButton = ( {selected, onClick }) => {
    const { pushPanel } = useSettingsContext();
    return <DefaultButton className="m-2" kind="add" variant={selected ? "primary" : "outline-primary"} onClick={() => { pushPanel(1); onClick() }}></DefaultButton>
}

const AcquireLocation = ({ onClick }) => {
    const { map } = useOL();

    useEffect(() => {
        const clickListener = e => {
            const coords = e.map.getCoordinateFromPixel(e.pixel);
            const [longitude, latitude] = toLonLat(coords);
            onClick?.({ latitude, longitude })
        }
        map.on('singleclick', clickListener)
        map.getTargetElement().style.cursor = 'crosshair';
        return () => {
            map.un('singleclick', clickListener);
            map.getTargetElement().style.cursor = null;
        }
    }, [ map, onClick ]);
}

const Locations = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const credentials = useCredentials();
    const locations = useLocations();
    const [selectedLocation, setSelectedLocation] = useState();
    const [newLocation, setNewLocation] = useState({ internalid: crypto.randomUUID() });
    const isNew = !selectedLocation;
    const [searchText, setSearchText] = useState('');
    const [sort, setSort] = useState(defaultSort);
    const [showSortDialog, setShowSortDialog] = useState(false);
    const [filter, setFilter] = useState({});
    const [editFilter, setEditFilter] = useState();

    const currentLocation = useMemo(() => isNew ? newLocation : locations.find(e => e.internalid === selectedLocation), [isNew, newLocation, locations, selectedLocation]);

    const sortFields = useMemo(() => {
        let fields = {name: t('name'), group: t('group')}
        if (clientConfig.enableLevels) {
            fields.level = t('level');
        }
        if (clientConfig.enableSites) {
            fields.site = t('site');
        }
        fields.type = t('type');
        return fields;
    }, [clientConfig.enableSites, clientConfig.enableLevels, t])

    let displayLocations = useMemo(() => {
        let displayLocations = deepClone(locations);
        displayLocations.sort(dynamicSort(sort));
        if (searchText) {
            displayLocations = dynamicFilter(displayLocations, searchText);
        }
        displayLocations = displayLocations.filter(l => ['site', 'level', 'type'].every(f => !filter[f] || !l[f] || l[f] === filter[f]));
        return displayLocations;
    }, [locations, searchText, sort, filter]);

    const setupNewLocation = () => {
        setSelectedLocation();
        setNewLocation(newLocation => ({ icon: newLocation?.icon, color: newLocation.color, internalid: crypto.randomUUID() }));
    }

    const disabled = useMemo(() =>  isNew ? !canInsertLocation(credentials, currentLocation) : !canUpdateLocation(credentials, currentLocation), [credentials, currentLocation, isNew]);
    const locationValid = currentLocation.name && currentLocation.color && currentLocation.icon;

    const handleUpdate = async delta => {
        if (clientConfig.altcoordMode !== 'none') {
            if ((delta.latitude || delta.longitude) && (delta.latitude ?? currentLocation.lat) && (delta.longitude ?? currentLocation.long)) {
                const res = await altCoord(network, delta.latitude ?? currentLocation.lat, delta.longitude ?? currentLocation.long)
                if (res?.addr) {
                    delta.w3w = res?.addr;
                }
            } else if (delta.w3w) {
                const searchResult = await geoSearch(network, delta.w3w)
                if (searchResult?.length === 1 && searchResult[0].display_name === delta.w3w) {
                    delta.latitude = searchResult[0].lat;
                    delta.longitude = searchResult[0].lon;
                }
            }
        }
        if (delta.latitude) {
            delta.lat = delta.latitude;
            delete delta.latitude
        }
        if (delta.longitude) {
            delta.long = delta.longitude;
            delete delta.longitude;
        }
        if (isNew) {
            setNewLocation(currentLocation => deepMerge({}, currentLocation, delta));
        } else {
            updateLocation(network, {internalid: currentLocation.internalid, ...delta});
        }
    }

    const handleAdd = () => {
        createLocation(network, newLocation);
        setupNewLocation();
    }

    const handleDelete = () => {
        removeLocation(network, currentLocation.internalid);
        setupNewLocation();
    }

    const controls = <Row className="mx-2">
        <Col xs='auto'>
            {isNew ? (canUpdateLocation(credentials, currentLocation) ? <DefaultButton kind="clear" onClick={setupNewLocation}></DefaultButton> : null) : (canRemoveLocation(credentials, currentLocation) ? <DefaultButton kind="delete" onClick={handleDelete}></DefaultButton> : null)}
        </Col>
        <Col></Col>
        <Col xs='auto'>
            {isNew && canInsertLocation(credentials, currentLocation) ? <DefaultButton kind="add" disabled={!locationValid} onClick={handleAdd}></DefaultButton> : null}
        </Col>
    </Row>

    const showSite = clientConfig.enableSites && displayLocations.some(l => l.site);
    const showLevel = clientConfig.enableLevels && displayLocations.some(l => l.level);
    const showType = displayLocations.some(l => l.type);
    const layers = useCallback((showLabel) => <RLayerVector visible={currentLocation?.latitude && currentLocation?.longitude}>
        <LocationMarker location={currentLocation} showLabel={showLabel} opacity={1} canUpdate={() => false}></LocationMarker>
    </RLayerVector>, [currentLocation])

    return <>
        <SortDialog show={showSortDialog} defaultOrder={defaultSort} sort={sort} fields={sortFields} title={t('userSort')} onClose={newSort => { if (newSort) { setSort(newSort); } setShowSortDialog(false); }} placeholder={t('sort')}></SortDialog>
        <ReactNodeDialog showModal={!!editFilter} title={t('filterConfig')} footerControls={<DefaultButton kind="clear" onClick={() => setEditFilter({})}></DefaultButton>} onClose={() => setEditFilter()} onSave={() => { setFilter({ ...editFilter }); setEditFilter() }}>
            { clientConfig.enableSites && <Row>
                <Col>
                    <SiteDropdown site={editFilter?.site} wildcard={t('anyWildcard')} onChange={v => setEditFilter(ef => ({ ...ef, ...v }))}></SiteDropdown>
                </Col>
            </Row> }
            { clientConfig.enableLevels && <Row>
                <Col>
                    <LevelDropdown site={editFilter?.site} level={editFilter?.level} wildcard={t('anyWildcard')} onChange={v => setEditFilter(ef => ({ ...ef, ...v }))}></LevelDropdown>
                </Col>
            </Row> }
            <Row>
                <Col>
                    <TypeDropdown site={editFilter?.site} level={editFilter?.level} type={editFilter?.type} wildcard={t('anyWildcard')} onChange={v => setEditFilter(ef => ({ ...ef, ...v }))}></TypeDropdown>
                </Col>
            </Row>
        </ReactNodeDialog>

        <SettingsDisplay uuid={uuid} title={label} onClose={onClose}>
            <div className="d-flex flex-column w-100 h-100">
                <Row>
                    <Col><TextInput value={searchText} live={true} hideLabel={true} placeholder={t('filter', { count: 1 })} onChange={setSearchText} prependCtrl={
                        <ButtonGroup>
                            <DefaultButton kind="sort" hideLabel={true} onClick={() => setShowSortDialog(true)}></DefaultButton>
                            <DefaultButton kind="filter" variant={filter?.site || filter?.level || filter?.type ? 'success' : 'default'} hideLabel={true} onClick={() => setEditFilter({ ...filter })}></DefaultButton>
                        </ButtonGroup>
                    }></TextInput></Col>
                </Row>
                {canInsertLocation(credentials) ? <NewLocationButton onClick={() => setupNewLocation()} selected={isNew}></NewLocationButton> : null}
                <div className="d-flex flex-column w=100" style={{ overflowY: 'auto', flex: 1 }}>
                    {displayLocations.map(l => <LocationButton key={l.internalid} location={l} showSite={showSite} showLevel={showLevel} showType={showType} onClick={() => setSelectedLocation(l.internalid)} selected={currentLocation?.internalid === l.internalid} setNewLocation={setupNewLocation}></LocationButton>)}
                </div>
            </div>
            <Allotment snap={true} vertical={true} defaultSizes={[35,65]}>
                <Allotment.Pane>
                    <div className="h-100 d-flex flex-column">
                        {controls}
                        <div className="mt-2" style={{ flex: 1, overflowY: 'auto' }}>
                            <LocationDetails key={currentLocation?.internalid} location={currentLocation} disabled={disabled} onChange={handleUpdate}></LocationDetails>
                        </div>
                    </div>
                </Allotment.Pane>
                <Allotment.Pane>
                    <BaseMap controls={<>
                        <DefaultView longitude={currentLocation.long} latitude={currentLocation.lat}></DefaultView>
                        <AcquireLocation onClick={pos => handleUpdate(pos)}></AcquireLocation>
                    </>} layers={layers}></BaseMap>
                </Allotment.Pane>
            </Allotment>
        </SettingsDisplay>
        </>
}
Locations.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}
export default Locations;